/* eslint-disable max-len */
import Icon from "../../components/Icon";
import { useMemo, useState, useEffect, useContext } from "react";
import { useScreenClass } from "react-grid-system";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { CustomBlock, StyledImg } from "../../components/CompetenciesList";
import FlexBox, { FlexGrow } from "../../components/FlexBox";
import TabGroup, { TabItem } from "../../components/TabGroup";
import { StyledText, TextSize } from "../../components/Text";
import theme from "../../styles/theme";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import SnackBar from "../../components/SnackBar";
import CreateCompetencySelection from "../../components/CreateCompetencySelection";
import CourseSkillsCreate from "../../components/CourseSkillsCreate";
import CourseOverviewCreate from "../../components/CourseOverviewCreate";
import axios from "axios";
import LoadingSpinner from "../../components/LoadingSpinner";
import { VersionContext } from "../../context/VersionContext";

const Header = styled.h4`
  text-align: center;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0px;
  color: ${theme.color.darkBlueGray900};
`;
const CustomFlex = styled(FlexBox)`
  padding: 20px;
  align-items: center;
  border-bottom: 1px solid #f0f6fe;
`;

const CustomGrow = styled(FlexGrow)`
  align-self: stretch;
  flex-grow: 0;
  border-right: 1px solid #f0f6fe;
`;

const Ul = styled.ul`
  color: ${theme.color.lightBlueGray500};
  padding-inline-start: 16px;
  margin-block-start: 0;
  margin-block-end: 0;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
`;
const LI = styled.li`
  color: ${theme.color.lightBlueGray500};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.20000000298023224px;
  text-align: left;
  vertical-align: text-top;
`;
const LiGradient = styled.li`
  font-style: normal;
  font-weight: 400;
  letter-spacing: 0.20000000298023224px;
  text-align: left;
  vertical-align: text-top;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent;
  margin-bottom: 5px;
  text-decoration: underline;
`;

interface CourseColumn {
  id: "title" | "number" | "pillar" | "competencies";
  label: string;
  minWidth?: number;
  align?: "right";
  format?: (value: number) => string;
}
const courseColumns: readonly CourseColumn[] = [
  { id: "title", label: "Course Title", minWidth: 200 },
  {
    id: "number",
    label: "Course Number",
    minWidth: 150,
  },
  { id: "pillar", label: "Pillar", minWidth: 80 },

  {
    id: "competencies",
    label: "Competencies",
    minWidth: 250,
  },
];
function CreateCompetency(this: any) {
  const navigate = useNavigate();
  const { competencyId } = useParams();
  const [tab, setTab] = useState(0);
  const screenClass = useScreenClass();
  const isMobile = useMemo(
    () => ["xs", "sm"].includes(screenClass),
    [screenClass]
  );
  const [navigatedCompetencies, setNavigatedCompetencies] = useLocalStorage(
    "competencies",
    []
  );
  const [dimensionError, setDimensionError] = useState<boolean>(false);
  const [image, setImage] = useState({ preview: "", raw: "" });
  const [loading, setLoading] = useState<boolean>(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);
  const [isErrorOpen, setIsErrorOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [selectedPillar, setSelectedPillar] = useState<{
    label: string;
    value: string;
    graphics: any;
    prefix: string;
  }>();
  const [selectedDomain, setSelectedDomain] = useState<{
    label: string;
    value: string;
  }>();
  const [selectedKnowledge, setSelectedKnowledge] = useState<{
    label: string;
    value: string;
  }>();
  const { version } = useContext(VersionContext);

  useEffect(() => {
    if (version.version_number === 0) {
      navigate("/curriculum/version");
    }
    setTab(0);
  }, []);
  const onImageChange = (e: any) => {
    const fileAsDataURL = window.URL.createObjectURL(e.target.files[0]);
    getHeightAndWidthFromDataUrl(fileAsDataURL).then((dimensions: any) => {
      if (dimensions.width === 341 && dimensions.height === 395) {
        setDimensionError(false);
        setImage({
          preview: URL.createObjectURL(e.target.files[0]),
          raw: e.target.files[0],
        });
      } else {
        setDimensionError(true);
      }
    });
  };
  const getHeightAndWidthFromDataUrl = (dataURL: any) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          height: img.height,
          width: img.width,
        });
      };
      img.src = dataURL;
    });
  const createNewCompetency = (data: any) => {
    setLoading(true);
    axios
      .post("/competency/new", data)
      .then((res) => {
        setMessage("Created competency successfully");
        setIsSnackbarOpen(true);
        setLoading(false);
        setTimeout(() => {
          navigate("/modify/competency");
        }, 400);
      })
      .catch(function (error) {
        if (error.response) {
          setMessage(
            error.response.data.error || "Failed to create competency!"
          );
          setIsErrorOpen(true);
        } else if (error.request) {
          console.log(error.request);
        } else {
          setMessage(error.response.data.error);
          setIsErrorOpen(true);
        }
        setLoading(false);
      });
  };
  return (
    <>
      <SnackBar
        isOpen={isSnackbarOpen}
        setOpen={setIsSnackbarOpen}
        message={message}
        type="success"
      />
      <SnackBar
        isOpen={isErrorOpen}
        setOpen={setIsErrorOpen}
        header="Error!"
        message={message}
        type="error"
      />
      <SnackBar
        isOpen={dimensionError}
        setOpen={setDimensionError}
        type="error"
        header="Wrong Dimensions"
        message="Make sure the competency card image dimensions are correct!"
      />
      <CustomFlex
        style={{
          minHeight: "inherit",
          height: "100%",
          borderBottom: "none",
          alignItems: "flex-start",
          overflowY: "scroll",
          padding: 0,
          background: "white",
        }}
      >
        {tab === 0 ? (
          <>
            <CreateCompetencySelection
              onNextClicked={(pillar, domain, knowledge) => {
                setSelectedPillar(pillar);
                setSelectedDomain(domain);
                setSelectedKnowledge(knowledge);
                setTab(1);
              }}
            />
          </>
        ) : (
          <>
            <FlexGrow
              className="visible-scrollbar"
              style={{
                overflowY: "scroll",
                // maxHeight: "calc(100vh - 128px)",
                minHeight: "calc(100vh - 30px)",
              }}
            >
              <CustomFlex>
                <FlexGrow>
                  <Header>Create New Competency</Header>
                </FlexGrow>
              </CustomFlex>
              {loading && <LoadingSpinner />}
              {tab === 1 && (
                <>
                  <CourseOverviewCreate
                    pillar={{
                      label: selectedPillar?.label,
                      value: selectedPillar?.value,
                      graphics: selectedPillar?.graphics,
                      prefix: selectedPillar?.prefix,
                    }}
                    subdomain_id={{
                      label: selectedDomain?.label,
                      value: selectedDomain?.value,
                    }}
                    area_id={{
                      label: selectedKnowledge?.label,
                      value: selectedKnowledge?.value,
                    }}
                    showBackButton
                    goBack={() => setTab(0)}
                    data={{}}
                    onSaveComplete={(data) => createNewCompetency(data)}
                  />
                </>
              )}
              {/* {tab === 2 && (
                <>
                  <CourseSkillsCreate
                    goBack={() => setTab(0)}
                    showBackButton
                    competencyData={{ pillar_title: selectedPillar?.label, subdomain_title: selectedDomain?.label, knowledge_area: selectedKnowledge?.label }}
                    code={''}
                    onSaveComplete={() => { }}
                  />
                </>
              )} */}
            </FlexGrow>
          </>
        )}
      </CustomFlex>
    </>
  );
}
export default CreateCompetency;
